import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { navBarColors, buttonsLinksColors, mobileBackgroundColors } from 'shared/constants/menu-colors';
import { mediaQueries } from 'shared/styles';
import { Select } from 'shared/components';
import { ConciergeScheme, resolveConciergeTheme } from '@dutchie/common/utils/concierge-luminance';
import { MobileAppFlair, MobileNavigationStyle } from 'types/graphql';
import { ActiveSwitch } from 'src/components/active-switch';
import { InlineBanner } from 'src/components/inline-banner';
import ImageUploadField from 'src/dispensary-admin/settings/mobile-app/image-upload-field';
import { useStores } from 'src/hooks/use-stores';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { uploadImage } from 'shared/graphql/misc/mutations';
import { radiusOptions, productCardButtonOptions } from '../helpers';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { PreviewSwitcher } from '../components/preview-switcher';
import { OptionPicker } from '../components/option-picker';
import { ColorPickerV2, ACCESSIBLE_COLORS_BANNER } from '../components/color-picker-v2';
import { ColorPickerCompact } from '../components/color-picker-compact';
import { MobileSettingsMovedBanner } from '../components/mobile-settings-moved-banner';
import { ProductCardButtonPicker } from '../components/product-card-button-picker';
import { LoadingFlairPreview } from '../components/loading-flair-preview';
import { BackgroundFlairPreview } from '../components/background-flair-preview';
import { TitleFlairPreview } from '../components/title-flair-preview';
import { ConciergePreview } from './concierge-preview';

import { usePreview } from '../components/preview/use-preview';
import { useApplyToAllLocations } from '../use-apply-to-all-locations';
import { useThemeForm } from './use-theme-form';

import { ThemeFormProps } from './theme.types';
import { Heading, Input, Inputs, Label, SubText } from '../customize.styles';

// Canvas each scheme falls back to when no color is stored — must match the
// preview/marketplace tokens (--dcm-concierge-forest-750 / --dcm-concierge-cream-50)
// so the checked swatch reflects what's actually displayed. Exported for tests.
export const conciergeCanvasDefaults: Record<ConciergeScheme, string> = {
  DARK: '#142922',
  LIGHT: '#fdf9f0',
};

// Presets per scheme, scheme default first — each must satisfy the scheme's
// luminance band so a preset click always yields a valid canvas;
// ColorPickerV2's custom color entry covers everything else. The two lists
// cover the same six hue families (green, neutral, cool slate, warm earth,
// plum, rose) and stay low-chroma because the preview derives surfaces and
// text tiers from the canvas via color-mix. Exported for tests.
export const conciergeCanvasPresets: Record<ConciergeScheme, string[]> = {
  DARK: [
    conciergeCanvasDefaults.DARK, // forest
    '#1c1c1e', // charcoal
    '#141821', // navy
    '#211a14', // espresso
    '#1d1526', // plum
    '#241419', // wine
  ],
  LIGHT: [
    conciergeCanvasDefaults.LIGHT, // cream
    '#ffffff', // white
    '#eef1f5', // cool grey
    '#eef4ee', // sage
    '#f4f0fa', // lavender
    '#faf0f2', // blush
  ],
};

// The palette always renders both schemes' presets in a stable order — the
// scheme simply follows whichever canvas is picked, so there's nothing to
// reshuffle. All presets are selectable; the advisory surfaces contrast
// concerns after selection instead of blocking choices.
const conciergeCanvasSwatches = [...conciergeCanvasPresets.DARK, ...conciergeCanvasPresets.LIGHT];

// Accent swatches lead with the default theme's golds — gold-400 (the stock
// accent, reads on dark canvases, and the selectedColor fallback below so an
// unset accent still checks the swatch that's actually rendering) and
// gold-800 (its deep counterpart for light canvases) — followed by the
// standard brand palette. All swatches are selectable; the advisory surfaces
// contrast concerns after selection. Exported for tests.
export const conciergeAccentSwatches = ['#d4a843', '#6c5320', ...buttonsLinksColors];

export function ThemeForm(props: ThemeFormProps): JSX.Element {
  const { mobileOnly = false } = props;
  const sectionName = 'Theme';
  const {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
    resetConciergeTheme,
    conciergeThemingEnabled,
    conciergeThemeAdvisory,
    guardConciergeTheme,
  } = useThemeForm(props);
  const { handleApplyToLocations, hasAccessToAllLocations, chainId, totalLocationCount } = useApplyToAllLocations({
    input: applyAllInput,
    afterApplyAll,
    sectionName,
  });
  const flags = useFlags();
  const { apolloClient, UI } = useStores();

  const { theme } = usePreview(previewData);
  const hasMobileApp = useHasMobileApp();
  const enableConcierge = Boolean(UI.dispensary.storeSettings?.enableConcierge);

  const tabShowFieldNames: Record<
    string,
    | 'mobileTabShowCart'
    | 'mobileTabShowConcierge'
    | 'mobileTabShowLoyalty'
    | 'mobileTabShowScanPay'
    | 'mobileTabShowSpecials'
  > = {
    specials: 'mobileTabShowSpecials',
    pay: 'mobileTabShowScanPay',
    cart: 'mobileTabShowCart',
    concierge: 'mobileTabShowConcierge',
    loyalty: 'mobileTabShowLoyalty',
  };

  const showPay = flags['fintech.retail-payments.scan-to-pay.rollout'] ?? false;
  const showMobileRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  const showAdvancedCustomization = flags['ecomm.app.advanced-customization'] ?? false;
  const mobileHeaderImageField = showMobileRedesign ? 'mobileHeaderImageUrlV2' : 'mobileHeaderImageUrl';

  const tabKeysToDefaults: Record<string, string> = {
    shop: 'Shop',
    specials: 'Specials',
    ...(showPay ? { pay: 'Pay' } : {}),
    cart: 'Cart',
    ...(enableConcierge ? { concierge: 'Concierge' } : {}),
    ...(showMobileRedesign ? { loyalty: 'Loyalty', more: 'More' } : { account: 'Account' }),
  };
  const buttonsLinksColor = useWatch<string>({ control, name: 'buttonsLinks', defaultValue: '' });
  const rawTitleFlairColor = useWatch<string>({ control, name: 'mobileTitleFlairColor', defaultValue: '' });
  const headerBackgroundColorForTitleFlair = useWatch<string>({
    control,
    name: 'mobileHeaderBackgroundColor',
    defaultValue: '',
  });
  const titleFlairColor = rawTitleFlairColor || headerBackgroundColorForTitleFlair || buttonsLinksColor;
  const mobileHeaderNavigationStyle = useWatch<MobileNavigationStyle>({
    control,
    name: 'mobileHeaderNavigationStyle',
    defaultValue: MobileNavigationStyle.platformDefault,
  });
  const showHeaderBackgroundColor = mobileHeaderNavigationStyle !== MobileNavigationStyle.liquidGlass;
  const showSpecials = useWatch({ control, name: 'mobileTabShowSpecials' });
  const showScanPay = useWatch({ control, name: 'mobileTabShowScanPay' });
  const showCart = useWatch({ control, name: 'mobileTabShowCart' });
  const showConcierge = useWatch({ control, name: 'mobileTabShowConcierge' });
  const showLoyalty = useWatch({ control, name: 'mobileTabShowLoyalty' });
  const showTabLabels = useWatch({ control, name: 'mobileShowTabLabels' });
  const conciergeAccentColor = useWatch<string>({ control, name: 'conciergeAccentColor', defaultValue: '' });
  const conciergeCanvasColor = useWatch<string>({ control, name: 'conciergeCanvasColor', defaultValue: '' });
  // The scheme isn't a field anywhere anymore — it follows the canvas, with
  // the dark default when no canvas is chosen.
  const conciergeScheme: ConciergeScheme = conciergeCanvasColor
    ? resolveConciergeTheme(conciergeCanvasColor).scheme
    : 'DARK';
  const isConciergeThemeDefault = !conciergeAccentColor && !conciergeCanvasColor;

  // Apply-to-all writes the same payload as publish, so it runs the same
  // concierge theme gate the hook applies to publish.
  const handleApplyToLocationsGuarded = (dispensaryIds: string[]): void => {
    if (!guardConciergeTheme()) {
      return;
    }
    handleApplyToLocations(dispensaryIds);
  };
  const enabledToggleableTabs =
    Number(Boolean(showSpecials)) +
    Number(Boolean(showCart)) +
    Number(enableConcierge && Boolean(showConcierge)) +
    Number(Boolean(showLoyalty)) +
    Number(showPay && Boolean(showScanPay));
  const hasReachedMaxEnabledTabs = enabledToggleableTabs >= 3;

  const { data } = props;
  const hasPartnerTheme = Boolean(data.remoteExtensions?.theme?.modulePath);

  // The concierge section renders as the layout's second row so its controls
  // and preview sit beside each other, below the main form/preview pair.
  // Gated on the hook's single derivation (per-store setting + rollout flag);
  // the mobile-app "Concierge" tab stays on enableConcierge alone — it
  // predates theming. AI Concierge theming isn't a "mobile app" setting (it's
  // its own separate chat feature's theme, shown across surfaces), so it's
  // hidden entirely in mobileOnly mode.
  const conciergeSection =
    conciergeThemingEnabled && !mobileOnly ? (
      <>
        <Heading>AI Concierge</Heading>
        <SubText>Customize the concierge chat to match your brand.</SubText>

        {hasPartnerTheme && (
          <ConciergePartnerThemeNote data-testid='concierge-partner-theme-note'>
            <StyledInlineBanner
              severity='info'
              description='This store uses a partner theme. If that theme declares <code>--dcm-concierge-theme-canvas</code> and <code>--dcm-concierge-theme-accent</code>, those colors take precedence over the selections here.'
            />
          </ConciergePartnerThemeNote>
        )}

        <Inputs>
          <Input>
            <Controller
              name='conciergeCanvasColor'
              control={control}
              render={({ onChange, value }) => (
                <ColorPickerV2
                  label='Background Color'
                  colors={conciergeCanvasSwatches}
                  onSelect={onChange}
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string is the unset form default
                  selectedColor={value || conciergeCanvasDefaults[conciergeScheme]}
                  allowCustomColorSelection
                />
              )}
            />
          </Input>

          <Input>
            <Controller
              name='conciergeAccentColor'
              control={control}
              render={({ onChange, value }) => (
                <ColorPickerV2
                  label='Accent Color'
                  colors={conciergeAccentSwatches}
                  onSelect={onChange}
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string is the unset form default
                  selectedColor={value || conciergeAccentSwatches[0]}
                  allowCustomColorSelection
                  helperText='Colors with strong contrast against your background are easiest to read.'
                />
              )}
            />
          </Input>

          {conciergeThemeAdvisory && (
            <StyledInlineBanner severity='warning' title='Concierge theme' description={conciergeThemeAdvisory} />
          )}

          <ResetThemeButton type='button' onClick={resetConciergeTheme} disabled={isConciergeThemeDefault}>
            Reset to default theme
          </ResetThemeButton>
        </Inputs>
      </>
    ) : undefined;

  const conciergePreview =
    conciergeThemingEnabled && !mobileOnly ? (
      <ConciergePreview
        accentColorHex={conciergeAccentColor}
        colorScheme={conciergeScheme}
        canvasColorHex={conciergeCanvasColor}
      />
    ) : undefined;

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how main elements such as buttons, cards and fields will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={<PreviewSwitcher previewData={previewData} mobileOnly={mobileOnly} />}
      secondaryContent={conciergeSection}
      secondaryPreviewComponent={conciergePreview}
      handleApplyToLocations={handleApplyToLocationsGuarded}
      hasAccessToAllLocations={hasAccessToAllLocations}
      chainId={chainId}
      totalLocationCount={totalLocationCount}
      hideHeader={mobileOnly}
    >
      {!mobileOnly && <MobileSettingsMovedBanner tabSegment='theme' />}

      {/* Style (corner radius) and the main Colors section below apply to the desktop/web
          storefront, not the mobile app specifically — hidden in mobileOnly mode. The "Buttons
          and Links (Shared)" control inside the Mobile App section further down still lets
          mobileOnly editors see/change that one shared value. */}
      {!mobileOnly && (
        <>
          <Inputs>
            <Input>
              <Controller
                name='defaultRadius'
                control={control}
                render={({ onChange, value }) => (
                  <OptionPicker label='Style' options={radiusOptions} onChange={onChange} selectedOption={value} />
                )}
              />
            </Input>
          </Inputs>

          <Heading>Colors</Heading>
          <SubText>Select colors to express your brand.</SubText>

          <Inputs>
            <Input>
              <Controller
                name='navBar'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2
                    label='Navigation Bar'
                    colors={navBarColors}
                    onSelect={onChange}
                    selectedColor={value}
                    allowCustomColorSelection
                  />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='buttonsLinks'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2
                    label='Buttons and Links'
                    colors={buttonsLinksColors}
                    onSelect={onChange}
                    selectedColor={value}
                  />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='productCardButton'
                control={control}
                render={({ onChange, value }) => (
                  <ProductCardButtonPicker
                    label='Product Card Button'
                    options={productCardButtonOptions}
                    onChange={onChange}
                    selectedOption={value}
                    theme={theme}
                  />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='staffPickTag'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2
                    label='Staff Pick Tag'
                    colors={buttonsLinksColors}
                    onSelect={onChange}
                    selectedColor={value}
                  />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='discountTag'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2
                    label='Discount Tag'
                    colors={buttonsLinksColors}
                    onSelect={onChange}
                    selectedColor={value}
                  />
                )}
              />
            </Input>
          </Inputs>
        </>
      )}

      {/* When ecomm.app.advanced-customization is on, mobile app settings live exclusively in
          the consolidated enterprise Mobile App tab (mobileOnly) — the classic
          /dispensaries/:id/customize/theme page shows the "They moved!" banner above instead of
          duplicating these fields. When the flag is off, the classic page still renders this
          section so the settings remain reachable. */}
      {hasMobileApp && (mobileOnly || !showAdvancedCustomization) && (
        <>
          {!mobileOnly && (
            <>
              <Heading>Mobile App</Heading>
              <SubText>Customize mobile app colors and styles.</SubText>
            </>
          )}

          <Inputs>
            {showAdvancedCustomization && (
              <Input>
                {/* Duplicate of the shared "Buttons and Links" control up in the main Colors
                    section above — same `buttonsLinks` field, rendered again here so editors
                    working through the new Mobile App / Advanced Customization settings can see
                    and change the value that also drives mobile buttons/links without scrolling
                    back up. Compact picker keeps this section from getting even longer. */}
                <Controller
                  name='buttonsLinks'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label='Buttons and Links (Shared)'
                      colors={buttonsLinksColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              </Input>
            )}

            {showMobileRedesign && (
              <Input>
                <Controller
                  name='mobileBackgroundColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label='Background Color'
                      colors={mobileBackgroundColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              </Input>
            )}

            {showAdvancedCustomization && (
              <>
                {showHeaderBackgroundColor && (
                  <Input>
                    <Controller
                      name='mobileHeaderBackgroundColor'
                      control={control}
                      render={({ onChange, value }) => (
                        <ColorPickerCompact
                          label='Header Background Color'
                          colors={navBarColors}
                          onSelect={onChange}
                          selectedColor={value}
                          allowCustomColorSelection
                        />
                      )}
                    />
                  </Input>
                )}

                <Input>
                  <Label htmlFor='mobileHeaderNavigationStyle'>Header Style</Label>
                  <Controller
                    name='mobileHeaderNavigationStyle'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select
                        id='mobileHeaderNavigationStyle'
                        onChange={onChange}
                        value={value}
                        width='100%'
                        fontSize={13}
                      >
                        <option value={MobileNavigationStyle.platformDefault}>Platform Default</option>
                        <option value={MobileNavigationStyle.liquidGlass}>Liquid Glass</option>
                        <option value={MobileNavigationStyle.solid}>Solid</option>
                      </Select>
                    )}
                  />
                </Input>
              </>
            )}

            <Input>
              <Label>Header Image</Label>
              <Controller
                name={mobileHeaderImageField}
                control={control}
                render={({ onChange, value }) => (
                  <ImageUploadField
                    fieldId={mobileHeaderImageField}
                    helperText='Less than 500x500px, .png only without background'
                    image={value}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    handleUpload={async (image) => {
                      const res = await apolloClient.mutate({
                        mutation: uploadImage,
                        variables: { input: { image } },
                      });

                      if (res.data?.uploadImage?.url) {
                        onChange(res.data.uploadImage.url);
                      }
                    }}
                  />
                )}
              />
            </Input>

            {showMobileRedesign && (
              <>
                <TwoColumnGrid>
                  <Input>
                    <Label htmlFor='mobileTitleFlair'>Title Decoration</Label>
                    <Controller
                      name='mobileTitleFlair'
                      control={control}
                      render={({ onChange, value }) => (
                        <>
                          <Select id='mobileTitleFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                            <option value={MobileAppFlair.none}>None</option>
                            <option value={MobileAppFlair.angled}>Angled</option>
                            <option value={MobileAppFlair.curved}>Curved</option>
                            <option value={MobileAppFlair.jagged}>Jagged</option>
                            <option value={MobileAppFlair.wavy}>Wavy</option>
                          </Select>
                          <TitleFlairPreview flairType={value} color={titleFlairColor} />
                        </>
                      )}
                    />
                  </Input>

                  <Input>
                    <Controller
                      name='mobileTitleFlairColor'
                      control={control}
                      render={({ onChange, value }) => (
                        <ColorPickerCompact
                          label='Title Decoration Color'
                          colors={buttonsLinksColors}
                          onSelect={onChange}
                          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string is the unset form default
                          selectedColor={value || titleFlairColor}
                          allowCustomColorSelection
                        />
                      )}
                    />
                  </Input>
                </TwoColumnGrid>

                <Input>
                  <Label htmlFor='mobileBackgroundFlair'>Background Decoration</Label>
                  <Controller
                    name='mobileBackgroundFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <>
                        <Select id='mobileBackgroundFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                          <option value={MobileAppFlair.none}>None</option>
                          <option value={MobileAppFlair.angled}>Angled</option>
                          <option value={MobileAppFlair.curved}>Curved</option>
                          <option value={MobileAppFlair.jagged}>Jagged</option>
                          <option value={MobileAppFlair.wavy}>Wavy</option>
                        </Select>
                        <BackgroundFlairPreview flairType={value} color={buttonsLinksColor} />
                      </>
                    )}
                  />
                </Input>

                <Input>
                  <Label htmlFor='mobileButtonFlair'>Button Decoration</Label>
                  <Controller
                    name='mobileButtonFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select id='mobileButtonFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                        <option value={MobileAppFlair.none}>None</option>
                        <option value={MobileAppFlair.angled}>Angled</option>
                        <option value={MobileAppFlair.curved}>Curved</option>
                        <option value={MobileAppFlair.jagged}>Jagged</option>
                        <option value={MobileAppFlair.wavy}>Wavy</option>
                      </Select>
                    )}
                  />
                </Input>

                <Input>
                  <Label htmlFor='mobileLoadingFlair'>Loading Decoration</Label>
                  <Controller
                    name='mobileLoadingFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <>
                        <Select id='mobileLoadingFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                          <option value={MobileAppFlair.none}>None</option>
                          <option value={MobileAppFlair.angled}>Angled</option>
                          <option value={MobileAppFlair.curved}>Curved</option>
                          <option value={MobileAppFlair.jagged}>Jagged</option>
                          <option value={MobileAppFlair.wavy}>Wavy</option>
                        </Select>
                        <LoadingFlairPreview flairType={value} color={buttonsLinksColor} />
                      </>
                    )}
                  />
                </Input>
              </>
            )}

            <Heading>{mobileOnly ? 'Tabs' : 'Mobile App Tabs'}</Heading>
            {showMobileRedesign && <SubText>Select up to 5 tabs to display</SubText>}

            <ThreeColumnGrid>
              <Input>
                <Controller
                  name='mobileNavigationBarBackgroundColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label='Tab Bar'
                      colors={navBarColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              </Input>

              <Input>
                <Controller
                  name='mobileNavigationBarActiveColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label='Active Tab'
                      colors={navBarColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              </Input>

              <Input>
                <Controller
                  name='mobileNavigationBarInactiveColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label='Inactive Tab'
                      colors={navBarColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              </Input>
            </ThreeColumnGrid>

            {showAdvancedCustomization && (
              <Input>
                <Label htmlFor='mobileBottomBarStyle'>Bottom Bar Style</Label>
                <Controller
                  name='mobileBottomBarStyle'
                  control={control}
                  render={({ onChange, value }) => (
                    <Select id='mobileBottomBarStyle' onChange={onChange} value={value} width='100%' fontSize={13}>
                      <option value={MobileNavigationStyle.platformDefault}>Platform Default</option>
                      <option value={MobileNavigationStyle.liquidGlass}>Liquid Glass</option>
                      <option value={MobileNavigationStyle.solid}>Solid</option>
                    </Select>
                  )}
                />
              </Input>
            )}

            {showMobileRedesign && (
              <Input>
                <Label>Show Tab Labels</Label>
                <Controller
                  name='mobileShowTabLabels'
                  control={control}
                  render={({ onChange, value }) => (
                    <ActiveSwitch name='mobileShowTabLabels' active={value} onClick={onChange} hasLabel />
                  )}
                />
              </Input>
            )}

            <TwoColumnGrid>
              {Object.entries(tabKeysToDefaults).map(([key, humanized]) => (
                <TabWrapper key={key}>
                  {showMobileRedesign &&
                    (key !== 'more' && key !== 'shop' ? (
                      <Input>
                        <Label htmlFor={`mobileTabShow${humanized}`}>Show {humanized}</Label>
                        <Controller
                          name={tabShowFieldNames[key]}
                          control={control}
                          render={({ onChange, value }) => (
                            <ToggleRow>
                              <ActiveSwitch
                                active={value}
                                disabled={hasReachedMaxEnabledTabs && !value}
                                onClick={onChange}
                                usePropForValue
                              />
                            </ToggleRow>
                          )}
                        />
                      </Input>
                    ) : (
                      <Input>
                        <Label htmlFor={`mobileTabShow${humanized}`}>Show {humanized}</Label>
                        <ToggleRow>
                          <ActiveSwitch active disabled onClick={() => undefined} usePropForValue />
                        </ToggleRow>
                      </Input>
                    ))}

                  {(!showMobileRedesign || showTabLabels) && (
                    <Input>
                      <Label htmlFor={`mobileTabLabel${humanized}`}>{humanized} Tab Label</Label>
                      <Controller
                        name={`mobileTabLabel${humanized}`}
                        control={control}
                        render={({ onChange, value }) => (
                          <StyledInput
                            type='text'
                            id={`mobileTabLabel${humanized}`}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={humanized}
                          />
                        )}
                      />
                    </Input>
                  )}
                </TabWrapper>
              ))}
            </TwoColumnGrid>
          </Inputs>
        </>
      )}

      <StyledInlineBanner severity='info' title='Important!' description={ACCESSIBLE_COLORS_BANNER} />
    </MenuCustomizationLayout>
  );
}

const StyledInlineBanner = styled(InlineBanner)`
  width: 100%;
`;

// Inputs has no top margin of its own, so the note needs its own gap.
const ConciergePartnerThemeNote = styled.div`
  margin-bottom: 20px;
`;

const ResetThemeButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: #0b99e6;
  cursor: pointer;
  font-size: 13px;
  line-height: 23px;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledInput = styled.input`
  border: 1px solid #d3d8de;
  border-radius: 4px;
  padding: 8px;
  width: 100%;
  max-width: 310px;
`;

const ToggleRow = styled.div`
  align-items: center;
  display: flex;
  min-height: 22px;
`;

// Two columns so paired fields (e.g. "Show Shop" / "Show Specials", or a decoration and its
// color) sit side by side at 50% width instead of stacking one per row.
const TwoColumnGrid = styled.div`
  display: grid;
  // minmax(0, 1fr), not plain 1fr -- without the explicit 0 minimum, a grid item's default
  // "auto" min-width is based on its content's min-content size, which can quietly grow the
  // column (and this container, and anything sized off it) to fit something like an expanded
  // ColorPickerCompact popover instead of letting it overlay in place.
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 20px;

  @media ${mediaQueries.custom(600)} {
    grid-template-columns: 1fr;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;

  @media ${mediaQueries.custom(600)} {
    grid-template-columns: 1fr;
  }
`;

// Column, not row: the toggle sits above its tab label text input within a cell (previously the
// two sat side by side in a row).
const TabWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
